<template>
  <div id="event-overview" class="brand-card-lg">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div>
          <p class="title">Feature Usage</p>
          <p class="subtitle mt-2">Which features are being used the most amongst your users.</p>
        </div>
        <div class="flex-col flex space-y-2">
          <div class="flex items-center space-x-2">
            <DatePickerCondensed @change="handleDateChange" :dateRange="[startDate, endDate]"></DatePickerCondensed>
            <!-- <SessionFilter @update="onFilterUpdate" :include="['trial', 'offerType', 'billingInterval', 'segmentId', 'response', 'planId']" /> -->
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading && !counts.length" class="-ml-4">
        <SessionListLoader />
      </div>
      <div v-else-if="counts.length && events.length" class="">
        <table class="min-w-full divide-y divide-brand-sand">
          <thead>
            <tr class="text-brand-brown text-sm font-semibold">
              <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0">
                <span class="sr-only">Order</span>
              </th>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left sm:pl-0">Feature</th>
              <th scope="col" class="px-3 py-3.5 text-left">Adoption Rate</th>
              <th scope="col" class="px-3 py-3.5 text-center">Usage</th>
              <th scope="col" class="px-3 py-3.5 text-center">Customers Used</th>
              <th scope="col" class="px-3 py-3.5 text-center">Trend</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-brand-sand bg-white">
            <template v-for="(event, index) in events.slice(0, numChartsShown)">
              <tr :key="`main-${event.event}`">
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-semibold bg-brand-light-sand border text-brand-brown border-brand-sand"
                  >
                    {{ index + 1 }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-5 text-sm text-gray-700 font-semibold">
                  {{ event.event }}
                </td>
                <td class="whitespace-nowrap px-3 py-5 text-sm text-gray-700 font-semibold w-48">
                  <el-progress
                    :percentage="
                      event.uniqueCustomers
                        ? Math.round((event.uniqueCustomers / (totalUnique.ids || totalUnique.customers)) * 100)
                        : Math.round((event.uniqueUsers / totalUnique.users) * 100)
                    "
                    :stroke-width="12"
                    class="w-full pr-2 unique-progress"
                  ></el-progress>
                </td>
                <td class="whitespace-nowrap px-8 py-5 text-sm text-brand-brown font-semibold text-center items-center space-x-2">
                  <span>
                    <font-awesome-icon :icon="['fas', 'plug']" class="text-brand-brown" />
                  </span>
                  <span>
                    {{ event.count | formatSmallNumber }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-8 py-5 text-sm text-brand-brown font-semibold text-center items-center space-x-2">
                  <span>
                    <font-awesome-icon :icon="['fas', 'users']" class="text-brand-brown" />
                  </span>
                  <span>
                    {{ formatSmallNumber(event.uniqueCustomers || event.uniqueUsers) }}
                  </span>
                </td>
                <td v-if="sparklineSpecs && sparklineSpecs[index]" class="whitespace-nowrap px-3 py-5 text-sm text-gray-700 font-semibold w-48">
                  <VueVegaEmbed :spec="sparklineSpecs[index]" width="auto" :height="60" />
                </td>
                <td @click="expandRow(index)" class="expand-control">
                  <font-awesome-icon :icon="['fas', expandedRows[index] ? 'chevron-up' : 'chevron-down']" class="text-brand-brown" />
                </td>
              </tr>
              <!-- <div :key="`expand-${event.event}`"> -->
              <!-- eslint-disable-next-line -->
              <transition name="expand">
                <tr v-if="expandedRows[index]" :key="`expanded-${event.event}`">
                  <td colspan="6">
                    <!-- Your additional details here. You can access the event data using events[index] -->
                    <VueVegaEmbed :spec="chartVegaSpecs[index]" width="auto" :height="120" />
                  </td>
                </tr>
              </transition>

              <!-- </div> -->
            </template>
          </tbody>
        </table>
        <div class="mt-4">
          <div class="flex justify-left" v-if="events.length > numChartsShown">
            <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown += 5">
              <span class="text-sm text-white font-semibold">Load More Events ↓</span>
            </el-button>
          </div>
          <div class="flex justify-left" v-else-if="events.length > 5">
            <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown = 5">
              <span class="text-sm text-white font-semibold">Hide Events ↑</span>
            </el-button>
          </div>
        </div>
      </div>
      <div v-else-if="false || (counts.length && events.length)" class="mt-6 grid grid-cols-3 gap-x-4 gap-y-6">
        <div v-for="(event, index) in events.slice(0, numChartsShown)" :key="event.event">
          <p class="header">{{ event.event }}</p>
          <p class="subheader">
            {{ event.count }} {{ event.count === 1 ? 'event' : 'events' }}
            <span v-if="event.uniqueCustomers || event.uniqueUsers"
              >from {{ event.uniqueCustomers || event.uniqueUsers }}
              {{
                event.uniqueCustomers ? (event.uniqueCustomers === 1 ? 'customer' : 'customers') : event.uniqueUsers === 1 ? 'user' : 'users'
              }}</span
            >
            in last 30 days
          </p>
          <!-- <p v-if="event.uniqueUsers" class="subheader">{{ event.uniqueUsers }} unique user(s) in last 30 days</p> -->
          <!-- <p class="subheader">{{ event.uniqueCustomers }} unique customer(s) in last 30 days</p> -->
          <div class="my-3" v-if="(event.uniqueCustomers && (totalUnique.ids || totalUnique.customers)) || (event.uniqueUsers && totalUnique.users)">
            <el-progress
              :percentage="
                event.uniqueCustomers
                  ? Math.round((event.uniqueCustomers / (totalUnique.ids || totalUnique.customers)) * 100)
                  : Math.round((event.uniqueUsers / totalUnique.users) * 100)
              "
              :stroke-width="10"
              class="w-full pr-2 unique-progress"
            ></el-progress>
          </div>
          <div class="mt-4">
            <VueVegaEmbed :spec="chartVegaSpecs[index]" width="auto" :height="180" />
          </div>
        </div>
        <div class="flex justify-left" v-if="events.length > numChartsShown">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown += 3">
            <span class="text-sm text-white font-semibold">Load More Event Charts ↓</span>
          </el-button>
        </div>
        <div class="flex justify-left" v-else-if="events.length > 3">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown = 3">
            <span class="text-sm text-white font-semibold">Hide Event Charts ↑</span>
          </el-button>
        </div>
      </div>
      <div v-else class="bg-white h-96"></div>
    </div>
  </div>
</template>

<script>
import { LOAD_STATE } from '@/store/shared';
import StatService from '@/api/stats.service';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
// import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import { timeDay } from 'd3-time';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import { mapGetters } from 'vuex';
import { EVENT_PROP } from '@/helpers/stats-utils';
import { genBarSpec } from '@/components/Vis/Vega/Specs/BarSpec';
import { genSparklineSpec } from '@/components/Vis/Vega/Specs/SparklineSpec';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { descending } from 'd3-array';
import { formatSmallNumber } from '@/helpers/jsUtils';

export default {
  name: 'FeatureUsage',
  components: {
    // SessionFilter,
    DatePickerCondensed,
    SessionListLoader,
    HeaderLoader,
    VueVegaEmbed,
  },
  props: {
    showExport: {
      type: Boolean,
      default: false,
    },
  },
  async created() {
    await this.fetchData();
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -30);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      filter: {},
      counts: [],
      expandedRows: [],
      dataLoadState: LOAD_STATE.UNLOADED,
      formatSmallNumber,
      startDate,
      endDate,
      initialLoading: true,
      totalUnique: {
        customers: 0,
        ids: 0,
        users: 0,
      },
      eventUnique: {},
      numChartsShown: 5,
    };
  },
  computed: {
    ...mapGetters(['daysSinceOrgCreated', 'orgCreatedDate']),
    days() {
      // list of days from startDate to endDate in form YYYY-MM-DD
      const days = [];
      let day = new Date(this.startDate);
      while (day <= this.endDate) {
        days.push(day.toISOString().split('T')[0]);
        day = timeDay.offset(day, 1);
      }
      return days;
    },
    loading() {
      return this.dataLoadState !== LOAD_STATE.LOADED;
    },
    eventData() {
      return this.counts.reduce((acc, c) => {
        if (!acc[c.event]) {
          acc[c.event] = 0;
        }
        acc[c.event] += c.count;
        return acc;
      }, {});
    },
    events() {
      // return list from eventData sorted by count
      return Object.keys(this.eventData)
        .map((event) => {
          const uniqueCounts = this.eventUnique[event];
          const uniqueCustomers = uniqueCounts ? Math.max(0, uniqueCounts.id, uniqueCounts.customer) : 0;
          const uniqueUsers = uniqueCounts ? uniqueCounts.user || 0 : 0;
          return { event, count: this.eventData[event], uniqueCustomers, uniqueUsers };
        })
        .filter((e) => e.event !== 'App Viewed')
        .sort((a, b) => descending(a.count, b.count));
    },
    chartVegaSpecs() {
      if (this.events && this.events.length) {
        return this.events.map((event) => {
          // combine with days to get a list of { day, count, event }
          const eventItems = this.counts.filter((c) => c.event === event.event);
          const allDays = this.days.map((day) => {
            const dayItem = eventItems.find((c) => c.day === day);
            return {
              day,
              count: dayItem ? dayItem.count : 0,
              event: event.event,
            };
          });
          return genBarSpec({
            data: allDays,
            xField: 'day',
            xSortField: 'day',
            yField: 'count',
            colorField: 'event',
            xLabels: false,
            yLabels: true,
            barLabels: true,
            yFormat: '0.2s',
            tooltipFields: [
              { field: 'day', title: 'Day', type: 'nominal' },
              { field: 'count', title: 'Count', type: 'quantitative' },
            ],
            color: '#3c5ccf',
          });
        });
      }
      return null;
    },
    sparklineSpecs() {
      if (this.events && this.events.length) {
        return this.events.map((event) => {
          // Combine with days to get a list of { day, count }
          const eventItems = this.counts.filter((c) => c.event === event.event);
          const allDays = this.days.map((day) => {
            const dayItem = eventItems.find((c) => c.day === day);
            return {
              day,
              count: dayItem ? dayItem.count : 0,
            };
          });
          return genSparklineSpec({
            data: allDays,
            xField: 'day',
            yField: 'count',
          });
        });
      }
      return null;
    },
  },
  methods: {
    async fetchData() {
      const breakdown = [EVENT_PROP.DAY, EVENT_PROP.EVENT_NAME];
      const mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      try {
        this.dataLoadState = LOAD_STATE.LOADING;
        const [counts, totalUnique, eventUnique] = await Promise.all([
          StatService.countEvents({ filter: mappedFilter, breakdown }),
          StatService.countEvents({ filter: mappedFilter, breakdown: [] }),
          StatService.countEvents({ filter: mappedFilter, breakdown: [EVENT_PROP.EVENT_NAME] }),
        ]);
        this.counts = counts;
        if (totalUnique[0]) {
          this.totalUnique = {
            customers: totalUnique[0].uniqueCustomerIdCount,
            ids: totalUnique[0].uniqueIdCount,
            users: totalUnique[0].uniqueUserIdCount,
          };
        }
        this.eventUnique = eventUnique.reduce((acc, e) => {
          acc[e.event] = {
            customer: e.uniqueCustomerIdCount,
            id: e.uniqueIdCount,
            user: e.uniqueUserIdCount,
          };
          return acc;
        }, {});
        this.dataLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.dataLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    handleDateChange(value) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchData();
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.fetchData();
    },
    expandRow(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
  },
};
</script>
<style lang="scss">
.activity-stream-search {
  @apply shadow-sm;
  @apply rounded-md;
  width: 20rem;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    line-height: 44px;
    height: 44px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}
.unique-progress {
  .el-progress__text {
    font-size: 13px !important;
    @apply text-brand-blue;
    @apply font-semibold;
    @apply pl-2;
  }
}
</style>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition:
    opacity 0.3s,
    height 0.3s;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  height: 0;
  transition:
    opacity 0.01s,
    height 0.01s;
}
</style>
